import { createAsyncThunk } from "@reduxjs/toolkit";
import { IDeleteApiResponse } from "../utils/api.types";
import { ISwitchAccount, IUserEmailSearch, IUserInviteToken } from "../utils/auth.types";
import axios from "../utils/axios";

import {
    IUserDetails,
    IUserDetailsResponse,
    IUserFormInputs,
    IUserInviteUpdatePayload,
    IUsersList,
    IUserUpdatePayload,
} from "../utils/user.types";

/**
 * Argument for fetchUsers when the caller needs server-side filtering/paging.
 * `params` is a raw query-string fragment, e.g. "search=ann&role=<id>&page=0&limit=10".
 */
export interface IUsersFetch {
    accountId: string;
    params?: string;
}

/**
 * Fetch Users async thunk.
*/
export const fetchUsers = createAsyncThunk(
    'user/fetchUsers',
    async (arg: string | IUsersFetch): Promise<IUsersList> => {
        // Accepts a bare accountId (existing callers) or { accountId, params } for
        // server-side search / role filter / pagination.
        const { accountId, params } = typeof arg === 'string' ? { accountId: arg, params: '' } : arg;
        const query = params ? `?${params}` : '';
        const response = await axios.get(`api/v1/user/${accountId}${query}`);

        return response.data;
    }
);

/**
 * Fetch User by user id async thunk.
*/
export const fetchUserById = createAsyncThunk(
    'user/fetchUserById',
    async (id: string): Promise<IUserDetails> => {
        const response = await axios.get(`api/v1/user/single/${id}`);

        return response.data.users;
    }
);

/**
 * Add User async Thunk.
*/
export const addUser = createAsyncThunk(
    'user/addUser',
    async (userReqPayload: IUserFormInputs): Promise<IUserDetailsResponse> => {
        const response = await axios.post('/api/v1/user', userReqPayload);

        return response.data;
    }
);

/**
 * Update User invite async Thunk.
*/

export const updateUserInvite = createAsyncThunk(
    'user/updateUserInvite',
    async ({ userId, accountId, userUpdatePayload }: IUserInviteUpdatePayload): Promise<IUserDetailsResponse> => {
        const response = await axios.put(`/api/v1/user/${userId}/invite/${accountId}`, userUpdatePayload);
        return response.data;
    }
);


/**
 * Update User async Thunk.
*/
export const updateUser = createAsyncThunk(
    'user/updateUser',
    async ({ id, userUpdatePayload }: IUserUpdatePayload): Promise<IUserDetailsResponse> => {
        const response = await axios.put(`/api/v1/user/${id}`, userUpdatePayload);

        return response.data;
    }
);

/**
 * Delete User async Thunk.
 */
export const deleteUser = createAsyncThunk(
    'user/deleteUser',
    async ({ accountId, userId }: ISwitchAccount): Promise<IDeleteApiResponse> => {
        const response = await axios.delete(`/api/v1/user/${userId}`, { params: { accountId } });

        response.data['id'] = userId;

        return response.data
    }
);

/**
 * Accept User Invite async Thunk.
 */
 export const verifyInviteToken = createAsyncThunk(
    'user/acceptInvite',
    async ({ token }: IUserInviteToken): Promise<IDeleteApiResponse> => {
        const response = await axios.post(`/api/v1/user/${token}/invite`, { params: { token } });
        return response.data
    }
);

export const fetchUserByEmail  = createAsyncThunk(
    'user/search',
    async ({ email }: IUserEmailSearch): Promise<IUsersList> => {
        const response = await axios.get(`/api/v1/user/search?email=${email}`);
        return response.data.users;
    }
);